import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Checkbox,
} from '@vnetwork/ui/primitives';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useServiceConfigoptions } from '@/api/services';
import { useMergeConfigoptions } from '@/api/services-mutations';

interface Props {
  id: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Merge configoptions dialog — lets the user pick ≥ 2 configoptions to merge
 * and nominate one to keep. Maps to POST /services/:id/configoptions/merge with
 * body { service_configoption_ids, keep_configoption_id }.
 *
 * Payload field names match the legacy `mergeServiceConfigoptions` function verbatim.
 */
export function StatementMergeConfigoptionsDialog({ id, open, onOpenChange }: Props) {
  const toast = useToast();
  const configs = useServiceConfigoptions(id);
  const merge = useMergeConfigoptions(id);

  // Multi-select tracked as a set of ids
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [keepId, setKeepId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setKeepId(null);
      setValidationError(null);
      merge.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // When selection changes, clear keepId if it's no longer in the selection
  useEffect(() => {
    if (keepId !== null && !selectedIds.has(keepId)) {
      setKeepId(null);
    }
  }, [selectedIds, keepId]);

  const options = useMemo(() => {
    return (configs.data?.results ?? []).map((c) => ({
      value: c.service_configoption_id,
      label: `${c.name} — ${c.commitment_value ?? 0} ${c.unit ?? ''}`.trim(),
    }));
  }, [configs.data]);

  const keepOptions = useMemo(() => {
    return options.filter((o) => selectedIds.has(o.value));
  }, [options, selectedIds]);

  const toggleId = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setValidationError(null);
  };

  const handleSubmit = () => {
    if (selectedIds.size < 2) {
      setValidationError('Select at least 2 configoptions to merge.');
      return;
    }
    if (!keepId) {
      setValidationError('Select which configoption to keep.');
      return;
    }
    if (!selectedIds.has(keepId)) {
      setValidationError('The configoption to keep must be in the selected list.');
      return;
    }

    setValidationError(null);
    merge.mutate(
      {
        service_configoption_ids: Array.from(selectedIds),
        keep_configoption_id: keepId,
      },
      {
        onSuccess: () => {
          toast.success('Configoptions merged successfully');
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error('Failed to merge configoptions', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Merge configoptions</DialogTitle>
          <DialogDescription>
            Select two or more configoptions to merge. Choose which one to keep —
            its commitment values will be preserved; the others will be removed.
          </DialogDescription>
        </DialogHeader>

        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Multi-select: checkboxes per configoption */}
          <Field label="Configoptions to merge">
            {(p) => (
              <div
                {...p}
                role="group"
                style={{
                  display: 'grid',
                  gap: 'var(--vn-space-2)',
                  border: '1px solid var(--vn-border-default)',
                  borderRadius: 'var(--vn-radius-md)',
                  padding: 'var(--vn-space-3)',
                  maxHeight: 192,
                  overflowY: 'auto',
                  background: 'var(--vn-bg-surface)',
                }}
              >
                {configs.isLoading && (
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                    Loading…
                  </span>
                )}
                {options.length === 0 && !configs.isLoading && (
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                    No configoptions available.
                  </span>
                )}
                {options.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-2)',
                      cursor: 'pointer',
                      fontSize: 'var(--vn-font-size-sm)',
                      color: 'var(--vn-fg-default)',
                    }}
                  >
                    <Checkbox
                      checked={selectedIds.has(opt.value)}
                      onCheckedChange={() => toggleId(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </Field>

          {/* Keep picker — only enabled once ≥ 2 selected */}
          <Field label="Keep this configoption">
            {(p) => (
              <Select
                value={keepId != null ? String(keepId) : ''}
                onValueChange={(v) => setKeepId(Number(v))}
                disabled={selectedIds.size < 2}
              >
                <SelectTrigger {...p} size="sm">
                  <SelectValue placeholder="Select which one to keep" />
                </SelectTrigger>
                <SelectContent>
                  {keepOptions.map((opt) => (
                    // value must be a non-empty string (Radix SelectItem trap)
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          {validationError && (
            <Alert tone="danger" title={validationError} />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={merge.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={merge.isPending || configs.isLoading}>
            {merge.isPending ? 'Merging…' : 'Merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
